import { useEffect } from "react";
import { useFetcher } from "react-router";

import type { loader } from "~/routes/resources/article-topic-title";

/**
 * SFCC-11240 (ARTICLES-13) — resolve a Learning Center article-topic slug to its
 * display title for the `LearningCenterArticleBreadcrumb` `article-topic` state.
 *
 * Mirrors the composable's `useFetchOneEntry(learning-center-article-topic)`
 * keyed on the `?topic=` slug, but via the Workers convention: a `useFetcher`
 * fronting the `/resources/article-topic-title` resource route (Workers has no
 * in-browser Builder fetch — see `article-grid.ts`). The fetcher key embeds the
 * slug so a topic switch creates a fresh fetcher and re-resolves.
 *
 * No-ops when `topicSlug` is undefined (the plain `/learning-center/articles`
 * listing with no topic filter), so the breadcrumb keeps its `Articles`
 * fallback without an unnecessary request.
 */

const RESOURCE_URL = "/resources/article-topic-title";

export interface UseTopicTitleResult {
  title: string | null;
  isLoading: boolean;
}

export function useTopicTitle(
  topicSlug: string | undefined,
): UseTopicTitleResult {
  const fetcher = useFetcher<typeof loader>({
    key: `article-topic-title-${topicSlug ?? "none"}`,
  });

  useEffect(() => {
    if (!topicSlug) return;
    fetcher.load(`${RESOURCE_URL}?topic=${encodeURIComponent(topicSlug)}`);
    // fetcher identity is stable per key; reload only when the slug changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicSlug]);

  // Guard on `topicSlug` so a no-op never hands back a title left over from a
  // previous fetch — the contract is "no title unless this slug was fetched".
  return {
    title: topicSlug ? (fetcher.data?.title ?? null) : null,
    isLoading: !!topicSlug && fetcher.state !== "idle" && !fetcher.data,
  };
}
